/**
 * StatusBadge — product dictionary for estimate, deployment, trip, ticket,
 * asset, invoice, and daily-report statuses. Apps should import this instead
 * of forking local pills.
 */
import { cn } from '../lib/utils';

const statusStyles: Record<string, string> = {
  // Success family
  active: 'bg-success-soft text-success',
  online: 'bg-success-soft text-success',
  healthy: 'bg-success-soft text-success',
  resolved: 'bg-success-soft text-success',
  paid: 'bg-success-soft text-success',
  accepted: 'bg-success-soft text-success',
  completed: 'bg-success-soft text-success',
  delivered: 'bg-success-soft text-success',

  // Warning family
  maintenance: 'bg-warning-soft text-warning',
  'at-risk': 'bg-warning-soft text-warning',
  outstanding: 'bg-warning-soft text-warning',
  pending: 'bg-warning-soft text-warning',
  mixed: 'bg-warning-soft text-warning',
  degraded: 'bg-warning-soft text-warning',
  viewed: 'bg-warning-soft text-warning',
  submitted: 'bg-warning-soft text-warning',
  'in-progress': 'bg-warning-soft text-warning',

  // Error family
  offline: 'bg-error-soft text-error',
  critical: 'bg-error-soft text-error',
  decommissioned: 'bg-error-soft text-error',
  breach: 'bg-error-soft text-error',
  rejected: 'bg-error-soft text-error',
  cancelled: 'bg-error-soft text-error',
  expired: 'bg-error-soft text-error',
  alert: 'bg-error-soft text-error',

  // Info family
  installing: 'bg-info-soft text-info',
  scheduled: 'bg-info-soft text-info',
  open: 'bg-info-soft text-info',
  sent: 'bg-info-soft text-info',
  info: 'bg-info-soft text-info',
  live: 'bg-success-soft text-success',
  ok: 'bg-success-soft text-success',
  stale: 'bg-warning-soft text-warning',
  triggered: 'bg-error-soft text-error',
  unconfigured: 'bg-surface-3 text-text-muted',

  // Internal / confidential / waiting
  internal: 'bg-accent-purple-soft text-accent-purple',
  confidential: 'bg-accent-purple-soft text-accent-purple',
  'waiting-on-customer': 'bg-accent-purple-soft text-accent-purple',
  premium: 'bg-accent-purple-soft text-accent-purple',

  // Priorities / SLA
  low: 'bg-surface-3 text-text-muted',
  medium: 'bg-accent-gold-soft text-accent-gold',
  high: 'bg-accent-orange-soft text-accent-orange',
  standard: 'bg-surface-3 text-text-muted',
  enterprise: 'bg-primary-soft text-primary',
  'all-ok': 'bg-success-soft text-success',

  // Neutral / muted
  void: 'bg-surface-3 text-text-muted',
  uncollectible: 'bg-error-soft text-error',
  draft: 'bg-surface-3 text-text-muted',
  inactive: 'bg-surface-3 text-text-muted',
  closed: 'bg-surface-3 text-text-muted',
  unknown: 'bg-surface-3 text-text-muted',
};

function formatLabel(status: string): string {
  return status
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface StatusBadgeProps {
  status: string;
  /**
   * Text to render instead of the humanised status. The apps are bilingual, so
   * a caller with a translated label passes it here; without it the behaviour
   * is unchanged and the status string is humanised as before.
   */
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const normalizedStatus = status.toLowerCase().replace(/_/g, '-');
  const style = statusStyles[normalizedStatus] || 'bg-surface-3 text-text-muted';

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium leading-none whitespace-nowrap',
        style,
        className,
      )}
    >
      {label ?? formatLabel(status)}
    </span>
  );
}
